import { s } from '@w/schema'
import { sql } from '@w/db'
import type { WorkspaceDb } from '../db/workspace-tables'
import { refreshPageSearch } from '../db/page-search-projection'
import { FLASHCARDS_EMBEDDING_BLOCK_MAX_CHARS, FLASHCARDS_EMBEDDING_DIMENSIONS, FLASHCARDS_EMBEDDING_INDEX, FLASHCARDS_EMBEDDING_TEXT_MAX_CHARS, pageEmbeddingIndexOptions } from '../db/embedding-schema'

// Module scope (Zod: define schemas once): inline schemas paid construction on every call.
const indexNameSchema = s.object({ name: s.string() })
const pendingEmbeddingSchema = s.object({ rowid: s.number(), generation: s.number(), text: s.string(), indexName: s.string() })
const storedEmbeddingSchema = s.object({ stored: s.number() })

export type PageEmbeddings = {
  embedDocument(text: string): Promise<number[]>
}

export type PageEmbeddingUpdateReport = {
  stored: number
  stale: number
  total: number
}

export type PendingPageEmbedding = { rowid: number; generation: number; text: string; indexName: string }
export type StoredPageEmbedding = { rowid: number; generation: number; vector: Uint8Array<ArrayBuffer>; indexName: string }
export type PageEmbeddingIndex = { name: string; vectorFn: 'vector8' | 'vector32'; dimensions: number }
const localIndex: PageEmbeddingIndex = FLASHCARDS_EMBEDDING_INDEX

/** Configuration event: replace only this slot; the engine queues live sources. */
export async function configurePageEmbeddingIndex(db: WorkspaceDb, index = localIndex): Promise<void> {
  const slot = index.name.split('/')[0]
  if (!index.name.startsWith(slot + '/') || !index.name.endsWith('/' + index.dimensions) || !Number.isSafeInteger(index.dimensions) || index.dimensions <= 0) {
    throw new Error('Embedding index requires model name and size')
  }
  const quote = (name: string) => '"' + name.replaceAll('"', '""') + '"'
  const installed = await db.query`SELECT name FROM sqlite_schema WHERE type = 'index'
    AND (lower(name) = ${slot.toLowerCase()} OR substr(lower(name), 1, ${slot.length + 1}) = ${slot.toLowerCase() + '/'})`
    .execute(indexNameSchema)
  if (installed.length === 1 && installed[0].name === index.name) return
  return db.transaction(async tx => {
    const previous = await tx.query`SELECT name FROM sqlite_schema WHERE type = 'index'
      AND (lower(name) = ${slot.toLowerCase()} OR substr(lower(name), 1, ${slot.length + 1}) = ${slot.toLowerCase() + '/'})`
      .execute(indexNameSchema)
    if (previous.length === 1 && previous[0].name === index.name) return
    for (const { name } of previous) await tx.query`${sql.raw(`DROP INDEX ${quote(name)}`)}`.run()
    await tx.query`${sql.raw(`CREATE INDEX IF NOT EXISTS ${quote(index.name)} ON "pageSearch" USING embedding ("title", "text") WITH (${pageEmbeddingIndexOptions(index)}) WHERE "pageSearch"."deletedAt" IS NULL;`)}`.run()
  }, { exclusive: true })
}

/** Rows a run reads at once; a caller that skips rows widens the read by their count so skipped rows never starve the rest. */
export const PENDING_PAGE_EMBEDDINGS_BATCH = 100

export async function readPendingPageEmbeddings(db: WorkspaceDb, index = localIndex, limit = PENDING_PAGE_EMBEDDINGS_BATCH): Promise<PendingPageEmbedding[]> {
  await refreshPageSearch(db)
  const current = await db.query`SELECT name FROM sqlite_schema WHERE type = 'index' AND name = ${index.name}`
    .execute(indexNameSchema)
  if (current.length === 0) return []
  return db.query`SELECT rowid, generation, text, ${index.name} AS indexName FROM embedding_pending(${index.name}, ${limit})`
    .execute(pendingEmbeddingSchema)
}

export function storePageEmbeddings(db: WorkspaceDb, rows: StoredPageEmbedding[], index = localIndex): Promise<number[]> {
  return db.transaction(async tx => {
    const current = await tx.query`SELECT name FROM sqlite_schema WHERE type = 'index' AND name = ${index.name}`
      .execute(indexNameSchema)
    if (current.length === 0) return rows.map(() => 0)
    const results: number[] = []
    for (const row of rows) {
      if (row.indexName !== index.name) { results.push(0); continue }
      const vector = index.vectorFn === 'vector8' ? sql`vector8(${row.vector})` : sql`vector32(${row.vector})`
      const result = await tx.query`SELECT embedding_store(${index.name},
        ${row.rowid}, ${row.generation}, ${vector}) AS stored`
        .executeOneOrThrow(storedEmbeddingSchema)
      results.push(result.stored)
    }
    return results
  })
}

export function encodeEmbedding(
  vector: number[],
  id: string,
  dimensions = FLASHCARDS_EMBEDDING_DIMENSIONS,
): Uint8Array<ArrayBuffer> {
  if (
    vector.length !== dimensions ||
    vector.some((value) => !Number.isFinite(value)) ||
    vector.every((value) => value === 0)
  ) {
    throw new Error(`Invalid embedding for ${id}`)
  }
  return new Uint8Array(new Float32Array(vector).buffer)
}

function limitEmbeddingTextBlocks(text: string): string {
  const separator = text.indexOf('\n')
  const title = separator < 0 ? text : text.slice(0, separator)
  const body = separator < 0 ? '' : text.slice(separator + 1)
  return title + '\n' + body
    .split(/\n{2,}/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) =>
      truncateEmbeddingText(part, FLASHCARDS_EMBEDDING_BLOCK_MAX_CHARS),
    )
    .join('\n\n')
}

function truncateEmbeddingText(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text
  const prefix = text.slice(0, maxChars)
  const boundaryWindowStart = Math.max(0, maxChars - 2_000)
  const boundaryCandidates = ['\n\n', '\n', '. ', '? ', '! ']

  for (const boundary of boundaryCandidates) {
    const index = prefix.lastIndexOf(boundary)
    if (index >= boundaryWindowStart) {
      return prefix.slice(0, index + boundary.trimEnd().length).trim()
    }
  }

  return prefix.trim()
}

export async function updateWorkspacePageEmbeddings(args: {
  db: WorkspaceDb
  embeddings: PageEmbeddings
  index?: PageEmbeddingIndex
  isClosed?: () => boolean
  skipped?: Set<string>
  accepts?: (text: string) => boolean
  isFinal?: (error: unknown, row: PendingPageEmbedding) => boolean
}): Promise<PageEmbeddingUpdateReport> {
  await configurePageEmbeddingIndex(args.db, args.index)
  return updatePageEmbeddings({
    read: limit => readPendingPageEmbeddings(args.db, args.index, limit),
    store: rows => storePageEmbeddings(args.db, rows, args.index),
    embeddings: args.embeddings,
    index: args.index,
    isClosed: args.isClosed,
    skipped: args.skipped,
    accepts: args.accepts,
    isFinal: args.isFinal,
  })
}

/** Identity of one queued version of a page's text: an edit enqueues a new generation, which is tried again. */
export function pendingEmbeddingKey(row: Pick<PendingPageEmbedding, 'rowid' | 'generation' | 'indexName'>): string {
  return `${row.indexName}:${row.rowid}:${row.generation}`
}

export async function updatePageEmbeddings(args: {
  read(limit: number): Promise<PendingPageEmbedding[]>
  store(rows: StoredPageEmbedding[]): Promise<number[]>
  embeddings: PageEmbeddings
  index?: PageEmbeddingIndex
  isClosed?: () => boolean
  /** Queued versions never sent again by this owner: text the model does not accept, or a final refusal (`isFinal`). */
  skipped?: Set<string>
  /** False: the model refuses this text (e.g. the server's blank-text rule), so the row is skipped, not sent. */
  accepts?: (text: string) => boolean
  /** True: this row's request can never succeed (e.g. the server refused it as invalid); skip the row and continue. */
  isFinal?: (error: unknown, row: PendingPageEmbedding) => boolean
}): Promise<PageEmbeddingUpdateReport> {
  let stored = 0
  let stale = 0
  const skipped = args.skipped ?? new Set<string>()
  while (!args.isClosed?.()) {
    const batch = (await args.read(PENDING_PAGE_EMBEDDINGS_BATCH + skipped.size)).filter(row => !skipped.has(pendingEmbeddingKey(row)))
    if (batch.length === 0) return { stored, stale, total: stored + stale }
    const embedded: StoredPageEmbedding[] = []
    for (const row of batch) {
      if (args.isClosed?.()) return { stored, stale, total: stored + stale }
      const text = preparePageEmbedding(row.text)
      if (args.accepts && !args.accepts(text)) { skipped.add(pendingEmbeddingKey(row)); continue }
      let vector: number[]
      try { vector = await args.embeddings.embedDocument(text) }
      catch (error) {
        if (!args.isFinal?.(error, row)) throw error
        skipped.add(pendingEmbeddingKey(row))
        continue
      }
      embedded.push({ rowid: row.rowid, generation: row.generation, indexName: row.indexName, vector: encodeEmbedding(vector, String(row.rowid), args.index?.dimensions) })
    }
    if (args.isClosed?.()) return { stored, stale, total: stored + stale }
    if (embedded.length === 0) continue
    const results = await args.store(embedded)
    for (const result of results) {
      if (result === 1) stored += 1
      else stale += 1
    }
  }
  return { stored, stale, total: stored + stale }
}

function preparePageEmbedding(text: string): string {
  if (text.length <= FLASHCARDS_EMBEDDING_TEXT_MAX_CHARS) return text
  const compact = limitEmbeddingTextBlocks(text)
  if (compact.length <= FLASHCARDS_EMBEDDING_TEXT_MAX_CHARS) return compact
  return truncateEmbeddingText(compact, FLASHCARDS_EMBEDDING_TEXT_MAX_CHARS)
}
